/*
 *  Copyright (c) 2014 The WebRTC project authors. All Rights Reserved.
 *
 *  Use of this source code is governed by a BSD-style license
 *  that can be found in the LICENSE file in the root of the source
 *  tree.
 */


const util = require('./util.js');

export function testMicrophone(test, deviceId) {
  const mictest = new MicTest(test, deviceId);
  mictest.run();
}

// mictest.js
function MicTest(test, deviceId) {
    this.test = test;
    this.inputChannelCount = 6;
    this.outputChannelCount = 2;
    // Buffer size set to 0 to let Chrome choose based on the platform.
    this.bufferSize = 0;
    this.deviceId = deviceId;
    // Turning off echoCancellation constraint enables stereo input.
    this.constraints = {
      audio: {
        optional: [
          { echoCancellation: false },
        ],
      },
    };

    this.collectSeconds = 2.0;
    // At least one LSB 16-bit data (compare is on absolute value).
    this.silentThreshold = 1.0 / 32767;
    this.lowVolumeThreshold = -60;
    // Data must be identical within one LSB 16-bit to be identified as mono.
    this.monoDetectThreshold = 1.0 / 65536;
    // Number of consequtive clipThreshold level samples that indicate clipping.
    this.clipCountThreshold = 6;
    this.clipThreshold = 1.0;

    // Populated with audio as a 3-dimensional array:
    //   collectedAudio[channels][buffers][samples]
    this.collectedAudio = [];
    this.collectedSampleCount = 0;
    for (let i = 0; i < this.inputChannelCount; ++i) {
      this.collectedAudio[i] = [];
    }
    const AudioContext = window.AudioContext || window.webkitAudioContext;
    this.audioContext = new AudioContext();
  }

  MicTest.prototype = {
    run() {
      // Resuming as per new spec after user interaction.
      this.audioContext.resume().then(() => {
        util.appendSourceId(this.deviceId, 'audio', this.constraints);
        util.doGetUserMedia(this.constraints, this.gotStream.bind(this), this.onFail.bind(this));
      })
      .catch((error) => {
        this.test.reportError(`WebAudio run failure: ${error}`);
        this.test.done();
      });
    },
    onFail(error) {
      this.test.reportError(`getUserMedia failed with error: ${error}`);
      this.test.done();
    },
    gotStream(stream) {
      if (!this.checkAudioTracks(stream)) {
        this.test.done();
        return;
      }
      this.createAudioBuffer(stream);
    },

    checkAudioTracks(stream) {
      this.stream = stream;
      const audioTracks = stream.getAudioTracks();
      if (audioTracks.length < 1) {
        this.test.reportError('No audio track in returned stream.');
        return false;
      }
      this.test.reportSuccess(`Audio track created using device=${
          audioTracks[0].label}`);
      return true;
    },

    createAudioBuffer() {
      this.audioSource = this.audioContext.createMediaStreamSource(this.stream);
      this.scriptNode = this.audioContext.createScriptProcessor(
        this.bufferSize,
        this.inputChannelCount, this.outputChannelCount,
      );
      this.audioSource.connect(this.scriptNode);
      this.scriptNode.connect(this.audioContext.destination);
      this.scriptNode.onaudioprocess = this.collectAudio.bind(this);
      this.stopCollectingAudio = util.setTimeoutWithProgressBar(this.onStopCollectingAudio.bind(this), 5000);
    },

    collectAudio(event) {
      // Simple silence detection: check first and last sample of each channel in
      // the buffer. If both are below a threshold, the buffer is considered
      // silent.
      const sampleCount = event.inputBuffer.length;
      let allSilent = true;
      for (let c = 0; c < event.inputBuffer.numberOfChannels; c++) {
        const data = event.inputBuffer.getChannelData(c);
        const first = Math.abs(data[0]);
        const last = Math.abs(data[sampleCount - 1]);
        var newBuffer;
        if (first > this.silentThreshold || last > this.silentThreshold) {
          // Non-silent buffers are copied for analysis. Note that the silent
          // detection will likely cause the stored stream to contain discontinu-
          // ities, but that is ok for our needs here (just looking at levels).
          newBuffer = new Float32Array(sampleCount);
          newBuffer.set(data);
          allSilent = false;
        } else {
          // Silent buffers are not copied, but we store empty buffers so that the
          // analysis doesn't have to care.
          newBuffer = new Float32Array();
        }
        this.collectedAudio[c].push(newBuffer);
      }
      if (!allSilent) {
        this.collectedSampleCount += sampleCount;
        if ((this.collectedSampleCount / event.inputBuffer.sampleRate) >=
            this.collectSeconds) {
          this.stopCollectingAudio();
        }
      }
    },

    onStopCollectingAudio() {
      this.stream.getAudioTracks()[0].stop();
      this.audioSource.disconnect(this.scriptNode);
      this.scriptNode.disconnect(this.audioContext.destination);
      this.analyzeAudio(this.collectedAudio);
      this.test.done();
    },

    analyzeAudio(channels) {
      const activeChannels = [];
      for (let c = 0; c < channels.length; c++) {
        if (this.channelStats(c, channels[c])) {
          activeChannels.push(c);
        }
      }
      if (activeChannels.length === 0) {
        this.test.reportError('No active input channels detected. Microphone ' +
            'is most likely muted or broken, please check if muted in the ' +
            'sound settings or physically on the device. Then rerun the test.');
      } else {
        this.test.reportSuccess(`Active audio input channels: ${
        activeChannels.length}`);
      }
      if (activeChannels.length === 2) {
        this.detectMono(channels[activeChannels[0]], channels[activeChannels[1]]);
      }
    },

    channelStats(channelNumber, buffers) {
      let maxPeak = 0.0;
      let maxRms = 0.0;
      let clipCount = 0;
      let maxClipCount = 0;
      for (let j = 0; j < buffers.length; j++) {
        const samples = buffers[j];
        if (samples.length > 0) {
          let s = 0;
          let rms = 0.0;
          for (let i = 0; i < samples.length; i++) {
            s = Math.abs(samples[i]);
            maxPeak = Math.max(maxPeak, s);
            rms += s * s;
            if (maxPeak >= this.clipThreshold) {
              clipCount += 1;
              maxClipCount = Math.max(maxClipCount, clipCount);
            } else {
              clipCount = 0;
            }
          }
          // RMS is calculated over each buffer, meaning the integration time will
          // be different depending on sample rate and buffer size. In practise
          // this should be a small problem.
          rms = Math.sqrt(rms / samples.length);
          maxRms = Math.max(maxRms, rms);
        }
      }

      if (maxPeak > this.silentThreshold) {
        const dBPeak = this.dBFS(maxPeak);
        const dBRms = this.dBFS(maxRms);
        this.test.reportInfo(`Channel ${channelNumber} levels: ${
            dBPeak.toFixed(1)} dB (peak), ${dBRms.toFixed(1)} dB (RMS)`);
        if (dBRms < this.lowVolumeThreshold) {
          this.test.reportError('Microphone input level is low, increase input ' +
              'volume or move closer to the microphone.');
        }
        if (maxClipCount > this.clipCountThreshold) {
          this.test.reportWarning('Clipping detected! Microphone input level ' +
              'is high. Decrease input volume or move away from the microphone.');
        }
        return true;
      }
      return false;
    },

    detectMono(buffersL, buffersR) {
      let diffSamples = 0;
      for (let j = 0; j < buffersL.length; j++) {
        const l = buffersL[j];
        const r = buffersR[j];
        if (l.length === r.length) {
          let d = 0.0;
          for (let i = 0; i < l.length; i++) {
            d = Math.abs(l[i] - r[i]);
            if (d > this.monoDetectThreshold) {
              diffSamples += 1;
            }
          }
        } else {
          diffSamples += 1;
        }
      }
      if (diffSamples > 0) {
        this.test.reportInfo('Stereo microphone detected.');
      } else {
        this.test.reportInfo('Mono microphone detected.');
      }
    },

    dBFS(gain) {
      //this.test.reportInfo( "dBFS" );
      const dB = 20 * (Math.log(gain) / Math.log(10));
      // Use Math.round to display up to one decimal place.
      return Math.round(dB * 10) / 10;
    },
  };



//////////////////
// WEBPACK FOOTER
// ./src/testWebRtc/mictest.js
// module id = 206
// module chunks = 0